import { useState, useEffect, useCallback, useRef } from 'react';
import { useGetImportStatusQuery } from '../Api/invoiceImportApi';
import { InvoiceImportStatus } from '../Types/invoiceImport.types';

/**
 * Configuration for status polling behavior
 */
interface StatusPollingConfig {
  /** Polling interval in milliseconds (default: 2000ms = 2s) */
  intervalMs?: number;
  /** Maximum retry attempts on network errors (default: 3) */
  maxRetries?: number;
  /** Enable exponential backoff on errors (default: true) */
  useExponentialBackoff?: boolean;
  /** Auto-start polling on mount (default: true) */
  autoStart?: boolean;
}

/**
 * Terminal statuses that stop polling
 */
const TERMINAL_STATUSES: InvoiceImportStatus[] = [
  InvoiceImportStatus.Completed,
  InvoiceImportStatus.Failed,
  InvoiceImportStatus.Duplicate,
  InvoiceImportStatus.RequiresReview,
  InvoiceImportStatus.Rejected,
  InvoiceImportStatus.Abandoned,
];

/**
 * Custom hook for auto-polling import job status
 *
 * Features:
 * - Auto-polling with configurable interval (default 2s)
 * - Stops polling on terminal statuses
 * - Exponential backoff on network errors
 * - Manual start/stop control
 *
 * @param importJobId - Import job ID to poll
 * @param config - Polling configuration
 */
export function useStatusPolling(importJobId: number | null, config: StatusPollingConfig = {}) {
  const {
    intervalMs = 2000,
    maxRetries = 3,
    useExponentialBackoff = true,
    autoStart = true,
  } = config;

  const [isPolling, setIsPolling] = useState(autoStart);
  const [retryCount, setRetryCount] = useState(0);
  const [currentInterval, setCurrentInterval] = useState(intervalMs);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // RTK Query hook - skip if not polling or no importJobId
  const {
    data: status,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetImportStatusQuery(importJobId!, {
    skip: !isPolling || !importJobId,
    // Disable automatic refetch - we control it manually
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * Check if current status is terminal (should stop polling)
   */
  const isTerminalStatus = useCallback((currentStatus?: InvoiceImportStatus) => {
    return currentStatus ? TERMINAL_STATUSES.includes(currentStatus) : false;
  }, []);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    setIsPolling(true);
    setRetryCount(0);
    setCurrentInterval(intervalMs);
  }, [intervalMs]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Calculate exponential backoff interval
   */
  const calculateBackoff = useCallback(
    (attempt: number): number => {
      if (!useExponentialBackoff) return intervalMs;
      // Exponential backoff: interval * 2^attempt, max 30s
      return Math.min(intervalMs * Math.pow(2, attempt), 30000);
    },
    [intervalMs, useExponentialBackoff]
  );

  /**
   * Polling effect
   */
  useEffect(() => {
    if (!isPolling || !importJobId) {
      return;
    }

    // Check if status is terminal
    if (status && isTerminalStatus(status.status)) {
      stopPolling();
      return;
    }

    // Set up polling timer
    timerRef.current = setTimeout(async () => {
      try {
        await refetch();
        // Reset retry count on successful fetch
        setRetryCount(0);
        setCurrentInterval(intervalMs);
      } catch (err) {
        // Handle network errors with exponential backoff
        const newRetryCount = retryCount + 1;

        if (newRetryCount >= maxRetries) {
          console.error('[useStatusPolling] Max retries reached, stopping polling');
          stopPolling();
          return;
        }

        setRetryCount(newRetryCount);
        const backoffInterval = calculateBackoff(newRetryCount);
        setCurrentInterval(backoffInterval);

        console.warn(
          `[useStatusPolling] Network error, retry ${newRetryCount}/${maxRetries} in ${backoffInterval}ms`
        );
      }
    }, currentInterval);

    // Cleanup timer on unmount or re-run
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    isPolling,
    importJobId,
    status,
    retryCount,
    maxRetries,
    currentInterval,
    intervalMs,
    refetch,
    isTerminalStatus,
    stopPolling,
    calculateBackoff,
  ]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    /** Current import status data */
    status,
    /** Loading state (initial fetch) */
    isLoading,
    /** Fetching state (refetch) */
    isFetching,
    /** Error from RTK Query */
    error,
    /** Is currently polling */
    isPolling,
    /** Current retry count (for exponential backoff) */
    retryCount,
    /** Current polling interval (ms) */
    currentInterval,
    /** Is status terminal (polling stopped) */
    isTerminal: status ? isTerminalStatus(status.status) : false,
    /** Manual start polling */
    startPolling,
    /** Manual stop polling */
    stopPolling,
    /** Manual refetch (one-time) */
    refetch,
  };
}
